import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

type RfidDrawerContextValue = {
  open: boolean
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  /** Desktop sidebar icon-rail collapse (persisted). */
  sidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void
}

const RfidDrawerContext = createContext<RfidDrawerContextValue | null>(null)

function readCollapsed(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}

export function RfidDrawerProvider({
  children,
  collapseStorageKey = 'rfid-tracking-re-fe.sidebarCollapsed',
}: {
  children: ReactNode
  collapseStorageKey?: string
}) {
  const [open, setOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readCollapsed(collapseStorageKey)
  )

  useEffect(() => {
    try {
      localStorage.setItem(collapseStorageKey, sidebarCollapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [collapseStorageKey, sidebarCollapsed])

  const openDrawer = useCallback(() => setOpen(true), [])
  const closeDrawer = useCallback(() => setOpen(false), [])
  const toggleDrawer = useCallback(() => setOpen((v) => !v), [])
  const toggleSidebarCollapsed = useCallback(
    () => setSidebarCollapsed((v) => !v),
    []
  )

  const value = useMemo(
    () => ({
      open,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      sidebarCollapsed,
      toggleSidebarCollapsed,
    }),
    [
      open,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      sidebarCollapsed,
      toggleSidebarCollapsed,
    ]
  )

  return <RfidDrawerContext.Provider value={value}>{children}</RfidDrawerContext.Provider>
}

export function useRfidDrawer(): RfidDrawerContextValue {
  const ctx = useContext(RfidDrawerContext)
  if (!ctx) throw new Error('useRfidDrawer must be used within RfidDrawerProvider')
  return ctx
}

export function useRfidDrawerOptional(): RfidDrawerContextValue | null {
  return useContext(RfidDrawerContext)
}
